import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface UseAutoLogoutOptions {
  timeoutMinutes?: number;
  bufferSeconds?: number;
  onLogout?: () => void;
}

interface UseAutoLogoutReturn {
  remainingTime: number; // in seconds
  isBufferPeriod: boolean;
}

const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'keyup',
  'keypress',
  'scroll',
  'touchstart',
  'touchmove',
  'click',
  'input',
  'change',
  'focus',
  'focusin',
  'select',
  'wheel',
];

export const useAutoLogout = ({
  timeoutMinutes = 60,
  bufferSeconds = 15,
  onLogout,
}: UseAutoLogoutOptions = {}): UseAutoLogoutReturn => {
  const navigate = useNavigate();
  const timeoutMs = timeoutMinutes * 60 * 1000;
  const bufferMs = bufferSeconds * 1000;
  
  const [remainingTime, setRemainingTime] = useState<number>(timeoutMinutes * 60);
  const [isBufferPeriod, setIsBufferPeriod] = useState<boolean>(true);
  
  const lastActivityRef = useRef<number>(Date.now());
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  const handleLogout = useCallback(() => {
    // Clear countdown timer
    if (countdownRef.current) clearInterval(countdownRef.current);
    
    // Call custom logout handler if provided
    if (onLogout) {
      onLogout();
    }
    
    // Clear storage and navigate to login
    sessionStorage.clear();
    localStorage.clear();
    navigate('/login');
  }, [navigate, onLogout]);

  useEffect(() => {
    const handleActivity = () => {
      lastActivityRef.current = Date.now();
      setIsBufferPeriod(true);
    };

    // Add event listeners for user activity
    ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Start the countdown interval
    countdownRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      
      // Check if we're still in buffer period
      if (elapsed < bufferMs) {
        setIsBufferPeriod(true);
        setRemainingTime(timeoutMinutes * 60);
        return;
      }
      
      // Buffer period has passed, start counting down
      setIsBufferPeriod(false);
      const elapsedAfterBuffer = elapsed - bufferMs;
      const remaining = Math.max(0, timeoutMs - elapsedAfterBuffer);
      const remainingSeconds = Math.ceil(remaining / 1000);
      
      setRemainingTime(remainingSeconds);
      
      if (remaining <= 0) {
        handleLogout();
      }
    }, 1000);

    // Capture ref value for cleanup
    const countdown = countdownRef.current;

    return () => {
      // Cleanup event listeners
      ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      
      // Clear countdown timer
      if (countdown) clearInterval(countdown);
    };
  }, [timeoutMs, bufferMs, timeoutMinutes, handleLogout]);

  return {
    remainingTime,
    isBufferPeriod,
  };
};

export default useAutoLogout;
